/**
 * Consumer Rewards Wallet — Iteration 42 (STEP 5 + STEP 8 marketplace).
 *
 * One shopper-facing surface for everything cashback-and-rewards:
 *   • KPI band          — Available · Pending · Lifetime · Adjustments
 *   • Ledger timeline   — chronological /rewards/ledger with hold countdowns,
 *                         status chips, adjustment badges
 *   • Gift-card catalog — mock catalogue with per-denomination redeem CTA
 *   • My gift cards     — issued codes with reveal + expiry
 *
 * Reads: /rewards/wallet, /rewards/ledger, /rewards/gift-cards/catalog,
 *        /rewards/my-gift-cards, /rewards/my-gift-cards/{id}
 * Writes: /rewards/gift-cards/redeem (POST)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Wallet, Gift, Clock, CheckCircle2, Undo2, ArrowUpRight, TrendingUp,
  Sparkles, ArrowRight, Copy, Eye, EyeOff, AlertCircle, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { GiftCardsComingSoon } from "@/components/rewards/GiftCardsComingSoon";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function fmt(minor, currency = "CAD") {
  return ((minor || 0) / 100).toLocaleString("en-CA",
    { style: "currency", currency });
}

const LEDGER_META = {
  CASHBACK_EARNED:    { icon: Sparkles,     tint: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Cashback earned" },
  CASHBACK_APPROVED:  { icon: CheckCircle2, tint: "bg-cyan-50 text-cyan-700 border-cyan-200",           label: "Cashback approved" },
  CASHBACK_ADJUSTED:  { icon: Undo2,        tint: "bg-amber-50 text-amber-700 border-amber-200",       label: "Partial refund adjustment" },
  CASHBACK_CANCELLED: { icon: X,            tint: "bg-slate-100 text-slate-500 border-slate-200",       label: "Cancelled — order refunded" },
  CASHBACK_REVERSED:  { icon: Undo2,        tint: "bg-rose-50 text-rose-700 border-rose-200",           label: "Reversed — refund after credit" },
  GIFT_CARD_REDEEMED: { icon: Gift,         tint: "bg-violet-50 text-violet-700 border-violet-200",    label: "Redeemed for a gift card" },
  MANUAL_ADJUSTMENT:  { icon: AlertCircle,  tint: "bg-slate-50 text-slate-600 border-slate-200",       label: "Manual adjustment" },
};

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  const days = Math.floor(diff / 86_400_000);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

function holdCountdown(availableAt) {
  if (!availableAt) return null;
  const d = new Date(availableAt).getTime() - Date.now();
  if (d <= 0) return "ready to approve";
  const days = Math.floor(d / 86_400_000);
  const hours = Math.floor((d % 86_400_000) / 3600_000);
  if (days > 0) return `available in ${days}d ${hours}h`;
  return `available in ${hours}h`;
}

export default function RewardsWallet() {
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [myCards, setMyCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [redeemProduct, setRedeemProduct] = useState(null);
  const [revealed, setRevealed] = useState({}); // {order_id: {code, pin}}

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [w, l, c, mc] = await Promise.all([
        api.get("/rewards/wallet"),
        api.get("/rewards/ledger?limit=25"),
        api.get("/rewards/gift-cards/catalog"),
        api.get("/rewards/my-gift-cards?limit=30"),
      ]);
      setWallet(w.data);
      setLedger(l.data.items || []);
      setCatalog(c.data);
      setMyCards(mc.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load your rewards wallet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (err && !wallet) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-sm text-rose-700"
           data-testid="rewards-error">{typeof err === "string" ? err : "Error loading wallet"}</div>
    );
  }
  if (loading && !wallet) {
    return <div className="max-w-5xl mx-auto px-6 py-16 text-sm text-slate-500"
                data-testid="rewards-loading">Loading your rewards…</div>;
  }

  const kpis = wallet?.kpis || {};
  const catalogKpis = catalog?.kpis || {};
  const giftCardsOn = !!catalog?.eligibility?.redemption_enabled;
  const affordableDenom = (d) => d <= (catalogKpis.available_minor || 0);

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="rewards-page">
      {/* --- Header --------------------------------------------------- */}
      <header className="mb-8">
        <div className="pf-chip pf-chip-cyan w-fit">
          <Gift className="w-3 h-3" /> Cyannect rewards
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight text-slate-900 mt-3">
          Your rewards wallet
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl leading-relaxed" data-testid="rewards-intro">
          {giftCardsOn
            ? <>Every verified purchase earns cashback. Once it clears the refund window, spend it
                on brand gift cards below — no waiting for a payout, no minimums beyond {fmt(catalogKpis.min_redemption_minor || 2000)}.</>
            : "Every verified purchase earns cashback. Once it clears the refund window, it shows as available here."}
        </p>
      </header>

      {/* --- KPIs ----------------------------------------------------- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Available" minor={kpis.available_minor}
                 accent="text-emerald-700" tone="from-emerald-50 to-emerald-100/60"
                 icon={Wallet} testid="rewards-kpi-available" />
        <KpiTile label="Pending"   minor={kpis.pending_minor}
                 accent="text-amber-700" tone="from-amber-50 to-amber-100/60"
                 icon={Clock} testid="rewards-kpi-pending" />
        <KpiTile label="Lifetime earned" minor={kpis.lifetime_earned_minor}
                 accent="text-cyan-700" tone="from-cyan-50 to-cyan-100/60"
                 icon={TrendingUp} testid="rewards-kpi-lifetime" />
        <KpiTile label="Adjustments" minor={kpis.adjustments_minor}
                 accent="text-slate-700" tone="from-slate-50 to-slate-100/60"
                 icon={Undo2} testid="rewards-kpi-adjustments" signed />
      </div>

      {/* --- Gift card marketplace (or "Coming soon" while redemption is off) --- */}
      {giftCardsOn ? (
      <section className="mt-10" data-testid="rewards-marketplace">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h2 className="font-display text-2xl text-slate-900">Gift card marketplace</h2>
            <p className="text-xs text-slate-500 mt-1">
              Trade your available cashback for a code you can use today.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Ready to spend</div>
            <div className="font-display text-xl text-emerald-700 tabular-nums" data-testid="rewards-spendable">
              {fmt(catalogKpis.available_minor)}
            </div>
          </div>
        </div>

        {(catalogKpis.available_minor || 0) < (catalogKpis.min_redemption_minor || 2000) && (
          <div className="pf-card p-4 mb-4 bg-gradient-to-r from-cyan-50 to-emerald-50 border-emerald-200/60"
               data-testid="rewards-below-threshold">
            <div className="text-xs text-slate-600">
              You need{" "}
              <span className="font-semibold text-slate-900">
                {fmt((catalogKpis.min_redemption_minor || 2000) - (catalogKpis.available_minor || 0))}
              </span>{" "}
              more available cashback to unlock gift-card redemption.
            </div>
            <div className="mt-2 h-2 bg-white/70 rounded-full overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all"
                   style={{ width: `${Math.min(100, ((catalogKpis.available_minor || 0) /
                     (catalogKpis.min_redemption_minor || 2000)) * 100).toFixed(1)}%` }} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(catalog?.products || []).map((p) => (
            <div key={p.id} className="pf-card overflow-hidden group"
                 data-testid={`gc-product-${p.brand.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
              <div className="h-32 relative overflow-hidden">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.brand}
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-display text-3xl"
                       style={{ backgroundColor: p.color || "#0e7490" }}>{p.brand}</div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-2 left-3 text-white font-display text-lg drop-shadow"
                     style={{ textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>{p.brand}</div>
              </div>
              <div className="p-4">
                <p className="text-xs text-slate-500 leading-snug min-h-[36px]">{p.description}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(p.denominations_minor || []).map((d) => (
                    <button
                      key={d}
                      disabled={!affordableDenom(d)}
                      onClick={() => setRedeemProduct({ product: p, denomination: d })}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums border transition
                        ${affordableDenom(d)
                          ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100 cursor-pointer"
                          : "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                      data-testid={`gc-denom-${p.brand.replace(/[^a-z0-9]/gi, "-").toLowerCase()}-${d}`}
                    >
                      {fmt(d)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      ) : <GiftCardsComingSoon />}

      {/* --- My gift cards ------------------------------------------- */}
      {myCards.length > 0 && (
        <section className="mt-10" data-testid="rewards-my-cards">
          <h2 className="font-display text-2xl text-slate-900 mb-4">Your gift cards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {myCards.map((c) => (
              <MyGiftCardRow key={c.id} order={c}
                              revealed={revealed[c.id]}
                              onReveal={async () => {
                                if (revealed[c.id]) {
                                  setRevealed((r) => ({ ...r, [c.id]: null }));
                                  return;
                                }
                                try {
                                  const r = await api.get(`/rewards/my-gift-cards/${c.id}`);
                                  setRevealed((prev) => ({ ...prev, [c.id]: r.data }));
                                } catch { toast.error("Could not reveal code."); }
                              }} />
            ))}
          </div>
        </section>
      )}

      {/* --- Ledger timeline ---------------------------------------- */}
      <section className="mt-10" data-testid="rewards-ledger">
        <h2 className="font-display text-2xl text-slate-900 mb-4">Recent activity</h2>
        {ledger.length === 0 ? (
          <div className="pf-card p-6 text-sm text-slate-500 text-center" data-testid="rewards-ledger-empty">
            No rewards activity yet. Redeem a Cyannect offer to earn your first cashback.
          </div>
        ) : (
          <div className="space-y-2">
            {ledger.map((entry) => (
              <LedgerRow key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </section>

      {/* --- Redeem confirmation modal ------------------------------ */}
      <RedeemModal
        open={!!redeemProduct}
        onOpenChange={(o) => !o && setRedeemProduct(null)}
        product={redeemProduct?.product}
        denomination={redeemProduct?.denomination}
        onSuccess={() => { setRedeemProduct(null); reload(); }}
      />
    </div>
  );
}

/* ---------------- sub-components ---------------- */

function KpiTile({ label, minor, accent, tone, icon: Icon, testid, signed }) {
  const val = signed ? minor : Math.max(0, minor || 0);
  const sign = signed && (minor || 0) < 0 ? "" : "";  // signed values keep the minus
  return (
    <div className={`pf-card p-4 bg-gradient-to-br ${tone}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className={`font-display text-2xl md:text-3xl font-medium tabular-nums mt-2 ${accent}`}>
        {sign}{fmt(val)}
      </div>
    </div>
  );
}

function LedgerRow({ entry }) {
  const meta = LEDGER_META[entry.type] || LEDGER_META.MANUAL_ADJUSTMENT;
  const Icon = meta.icon;
  const isDebit = (entry.amount_minor || 0) < 0;
  return (
    <div className="pf-card p-4 flex items-center gap-4"
         data-testid={`rewards-ledger-row-${entry.id}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${meta.tint}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">
          {meta.label}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span>{relativeTime(entry.created_at)}</span>
          {entry.metadata?.brand && (
            <span className="pf-chip pf-chip-neutral">{entry.metadata.brand}</span>
          )}
          {entry.reason && (
            <span className="text-slate-400">· {entry.reason.replaceAll("_", " ")}</span>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className={`font-display text-lg tabular-nums ${
          isDebit ? "text-slate-600" :
          entry.type === "CASHBACK_EARNED" ? "text-emerald-700" : "text-slate-900"
        }`}>
          {isDebit ? "-" : "+"}{fmt(Math.abs(entry.amount_minor), entry.currency)}
        </div>
      </div>
    </div>
  );
}

function MyGiftCardRow({ order, revealed, onReveal }) {
  const isFulfilled = order.status === "FULFILLED";
  const shownCode = revealed?.code || order.code_masked || "";
  const copy = async () => {
    if (!revealed?.code) return;
    try {
      await navigator.clipboard.writeText(revealed.code);
      toast.success("Code copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };
  return (
    <div className="pf-card p-4 flex items-center gap-4"
         data-testid={`my-gc-${order.id}`}>
      <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-display shrink-0"
           style={{ backgroundColor: order.color || "#0e7490" }}>
        <Gift className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
          {order.brand}
          <span className="text-xs text-slate-400 tabular-nums">· {fmt(order.denomination_minor, order.currency)}</span>
        </div>
        {isFulfilled ? (
          <div className="mt-1 flex items-center gap-2">
            <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded text-slate-700" data-testid={`my-gc-code-${order.id}`}>{shownCode}</span>
            <button onClick={onReveal} className="text-xs text-cyan-700 hover:text-cyan-900 flex items-center gap-1"
                    data-testid={`my-gc-reveal-${order.id}`}>
              {revealed ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Reveal</>}
            </button>
            {revealed?.code && (
              <button onClick={copy} className="text-xs text-cyan-700 hover:text-cyan-900 flex items-center gap-1"
                      data-testid={`my-gc-copy-${order.id}`}>
                <Copy className="w-3 h-3" /> Copy
              </button>
            )}
            {revealed?.pin && (
              <span className="text-xs text-slate-500">PIN: <span className="font-mono">{revealed.pin}</span></span>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-500 mt-1">
            {order.status === "PENDING" ? "Provisioning…" :
             order.status === "FAILED" ? "Provider unavailable — balance refunded" :
             order.status === "VOIDED" ? "Voided" : order.status}
          </div>
        )}
      </div>
      {order.expires_at && isFulfilled && (
        <div className="text-[10px] text-slate-400">
          Expires {new Date(order.expires_at).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

function RedeemModal({ open, onOpenChange, product, denomination, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [stepUpNeeded, setStepUpNeeded] = useState(false);
  const [stepUpPassword, setStepUpPassword] = useState("");
  const [lockedMsg, setLockedMsg] = useState(null);

  // Reset state whenever the modal reopens for a fresh product.
  useEffect(() => {
    if (open) { setStepUpNeeded(false); setStepUpPassword(""); setLockedMsg(null); }
  }, [open, product?.id, denomination]);

  const submit = async (stepUpToken) => {
    setSubmitting(true);
    try {
      const payload = { product_id: product.id, denomination_minor: denomination };
      if (stepUpToken) payload.step_up_token = stepUpToken;
      const r = await api.post("/rewards/gift-cards/redeem", payload);
      toast.success(`${product.brand} gift card redeemed. Check "Your gift cards".`);
      onSuccess?.(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (e.response?.status === 401 && code === "step_up_required") {
        setStepUpNeeded(true);
      } else if (e.response?.status === 423 && code === "account_locked") {
        setLockedMsg(detail.detail);
      } else {
        const msg = typeof detail === "string" ? detail : detail?.detail || "Redemption failed";
        toast.error(msg);
      }
    } finally { setSubmitting(false); }
  };

  const verifyStepUpAndRedeem = async () => {
    if (!stepUpPassword) { toast.error("Password required."); return; }
    setSubmitting(true);
    try {
      const t = await api.post("/auth/step-up", { password: stepUpPassword });
      await submit(t.data.step_up_token);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.detail || "Verification failed");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="redeem-modal">
        <DialogHeader>
          <DialogTitle>{stepUpNeeded ? "Verify it's you" : "Confirm redemption"}</DialogTitle>
          <DialogDescription>
            {stepUpNeeded
              ? "For your security, please re-enter your password to authorise this high-value redemption."
              : "Trade your cashback for a " + (product?.brand || "") + " gift card. Codes are issued instantly and never expire in less than 12 months."}
          </DialogDescription>
        </DialogHeader>
        {lockedMsg && (
          <div className="pf-card p-4 bg-rose-50 border-rose-200" data-testid="redeem-locked">
            <div className="flex items-center gap-2 text-rose-800 font-semibold">
              <AlertCircle className="w-4 h-4" /> Account temporarily locked
            </div>
            <div className="text-xs text-rose-700 mt-1">{lockedMsg}</div>
          </div>
        )}
        {product && !stepUpNeeded && !lockedMsg && (
          <div className="pf-card p-4 flex items-center gap-4 bg-gradient-to-r from-slate-50 to-white">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-display shrink-0"
                 style={{ backgroundColor: product.color || "#0e7490" }}>
              <Gift className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg text-slate-900">{product.brand}</div>
              <div className="text-xs text-slate-500">{product.description}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Amount</div>
              <div className="font-display text-2xl tabular-nums text-emerald-700">{fmt(denomination)}</div>
            </div>
          </div>
        )}
        {stepUpNeeded && !lockedMsg && (
          <div>
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-widest">Your password</label>
            <input type="password" value={stepUpPassword}
                   onChange={(e) => setStepUpPassword(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") verifyStepUpAndRedeem(); }}
                   autoFocus
                   className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1"
                   placeholder="••••••••"
                   data-testid="redeem-step-up-password" />
          </div>
        )}
        {!stepUpNeeded && !lockedMsg && (
          <div className="text-xs text-slate-500 leading-relaxed">
            Your cashback balance will decrease by <span className="font-semibold text-slate-900">{fmt(denomination)}</span> immediately.
            A refund of the underlying purchase after this point <span className="italic">will not</span> reverse the gift card — the platform absorbs the difference.
          </div>
        )}
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => onOpenChange(false)}
                  className="px-4 py-2 rounded-full text-sm border border-slate-200 hover:bg-slate-50 transition"
                  data-testid="redeem-cancel">Cancel</button>
          {!lockedMsg && (
            <button
              onClick={() => stepUpNeeded ? verifyStepUpAndRedeem() : submit()}
              disabled={submitting}
              className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="redeem-confirm">
              {submitting ? "Redeeming…" :
                stepUpNeeded ? (<>Verify & redeem <ArrowRight className="w-3.5 h-3.5" /></>) :
                (<>Redeem <ArrowRight className="w-3.5 h-3.5" /></>)}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
